import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, DoorClosed, DoorOpen, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';
import type { Heat } from '../../../types/heats';

/** Une vague en cours ou terminée ne se replanifie pas : elle porte des résultats. */
const VERROUILLES = new Set(['preparing', 'countdown', 'running', 'finished', 'completed']);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heats: Heat[];
  loading: boolean;
  onConfirm: (openIds: string[], closeIds: string[]) => void;
}

/** Libellé de la catégorie d'une vague — une heat de groupe n'a pas de `category_id`. */
const nomCategorie = (heat: Heat): string => {
  if (heat.is_multi_category && heat.category_names?.length) return heat.category_names.join(' / ');
  return heat.category_name || '—';
};
const cleCategorie = (heat: Heat): string =>
  (heat.category_group_id || heat.category_id || 'sans') as string;

/** Pastille de statut, atténuée pour la valeur qui s'en va. */
const Pastille: React.FC<{ libelle: string; sortante?: boolean; barre?: boolean }> = ({
  libelle, sortante, barre,
}) => (
  <span
    className={cn(
      'inline-block rounded-sm border px-2 py-0.5 text-[11px] whitespace-nowrap',
      sortante
        ? 'border-border text-muted-foreground'
        : 'border-border font-semibold text-foreground',
      barre && 'line-through',
    )}
  >
    {libelle}
  </span>
);

/**
 * Vague dont le créneau ne se modifie pas ici : elle court, elle est terminée
 * (elle porte des résultats), ou elle est annulée — la ressusciter au détour
 * d'une case à cocher serait décider à la place de l'organisateur.
 */
const estFigee = (heat: Heat): boolean => {
  const statut = (heat.status || '').toLowerCase();
  return VERROUILLES.has(statut) || statut === 'cancelled';
};

const Fleche = () => (
  <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
);

/** La porte du planning : verte ouverte, grise fermée. La COULEUR est le signal. */
const Porte: React.FC<{ ouverte: boolean }> = ({ ouverte }) =>
  ouverte
    ? <DoorOpen className="h-4 w-4 shrink-0 text-success" aria-hidden />
    : <DoorClosed className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />;

/**
 * Ouvrir des créneaux d'inscription en un geste.
 *
 * Avant cette fenêtre il en fallait DEUX, sur deux écrans : publier la vague au
 * planning, puis cliquer sa porte. Rien ne le disait, et le créneau restait
 * invisible à l'inscription pendant que l'organisateur croyait avoir fini.
 *
 * **L'action est asymétrique**, et c'est la seule règle non devinable :
 *   cocher   → la vague est PUBLIÉE au planning public ET sa porte s'ouvre ;
 *   décocher → la porte se referme, la vague RESTE publiée.
 * Dépublier retirerait du planning un horaire que des athlètes ont déjà vu.
 *
 * D'où la lecture `avant → après` sur les deux colonnes de résultat, mais
 * seulement sur les lignes qu'on a touchées : l'arrivée du chevron EST le
 * signal qu'un changement attend d'être appliqué. Une ligne modifiée porte en
 * plus un liseré blanc — un état d'écran, jamais l'or ni le dégradé, qui sont
 * réservés aux CTA et au rang 1.
 */
const SlotsDialog: React.FC<Props> = ({ open, onOpenChange, heats, loading, onConfirm }) => {
  const { t } = useTranslation();
  const [coches, setCoches] = React.useState<Set<string>>(new Set());
  const [categorie, setCategorie] = React.useState<string>('all');

  // L'état de départ, celui de la base. Tout le reste de la fenêtre est l'ÉCART
  // avec lui : la fenêtre s'ouvre donc en reflétant la réalité, et l'organisateur
  // ne voit de flèche que là où il a lui-même changé quelque chose.
  const ouvertsALOuverture = React.useMemo(
    () => new Set(heats.filter(h => h.slot_open).map(h => h.id)),
    [heats],
  );

  React.useEffect(() => {
    if (open) {
      setCoches(new Set(ouvertsALOuverture));
      setCategorie('all');
    }
  }, [open, ouvertsALOuverture]);

  const categories = React.useMemo(() => {
    const vues = new Map<string, string>();
    heats.forEach(h => vues.set(cleCategorie(h), nomCategorie(h)));
    return Array.from(vues, ([id, name]) => ({ id, name }));
  }, [heats]);

  const visibles = React.useMemo(
    () => heats.filter(h => categorie === 'all' || cleCategorie(h) === categorie),
    [heats, categorie],
  );

  const basculer = (heat: Heat) => {
    if (estFigee(heat)) return;
    setCoches(prev => {
      const suivant = new Set(prev);
      if (suivant.has(heat.id)) suivant.delete(heat.id); else suivant.add(heat.id);
      return suivant;
    });
  };

  // Le diff. On n'envoie que ce qui a bougé : une vague modifiée entre-temps
  // depuis le planning n'est pas écrasée par un état que la fenêtre avait figé
  // à son ouverture.
  const aOuvrir = React.useMemo(
    () => heats.filter(h => !estFigee(h) && coches.has(h.id) && !ouvertsALOuverture.has(h.id)),
    [heats, coches, ouvertsALOuverture],
  );
  const aFermer = React.useMemo(
    () => heats.filter(h => !estFigee(h) && !coches.has(h.id) && ouvertsALOuverture.has(h.id)),
    [heats, coches, ouvertsALOuverture],
  );
  const aPublier = aOuvrir.filter(h => (h.status || '').toLowerCase() !== 'ready').length;
  const rien = aOuvrir.length === 0 && aFermer.length === 0;

  // Cocher tout ne porte que sur ce qui est AFFICHÉ : cocher en masse des vagues
  // masquées par le filtre serait décider sans voir.
  const cochables = visibles.filter(h => !estFigee(h));
  const toutCoche = cochables.length > 0 && cochables.every(h => coches.has(h.id));

  const libelleStatut = (heat: Heat) =>
    t(`heats.statusLabels.${heat.status}`, { defaultValue: heat.status });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {t('heats.slotsDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm leading-relaxed text-muted-foreground">
          {t('heats.slotsDialog.rule')}
        </p>

        {categories.length > 1 && (
          <div className="flex items-center gap-3">
            <span className="text-sm">{t('heats.slotsDialog.category')}</span>
            <Select value={categorie} onValueChange={setCategorie}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('heats.slotsDialog.allCategories')}</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="max-h-[45vh] overflow-y-auto rounded border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={toutCoche}
                    onCheckedChange={() => setCoches(prev => {
                      const suivant = new Set(prev);
                      cochables.forEach(h => {
                        if (toutCoche) suivant.delete(h.id); else suivant.add(h.id);
                      });
                      return suivant;
                    })}
                    aria-label={t('heats.slotsDialog.selectAll')}
                  />
                </TableHead>
                <TableHead className="w-20">{t('heats.table.time')}</TableHead>
                <TableHead>{t('heats.slotsDialog.wave')}</TableHead>
                <TableHead className="w-24">{t('heats.table.participants')}</TableHead>
                <TableHead className="w-44">{t('heats.status')}</TableHead>
                <TableHead className="w-28">{t('heats.slotsDialog.slotColumn')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibles.map(heat => {
                const figee = estFigee(heat);
                const coche = coches.has(heat.id);
                const ouverteAvant = ouvertsALOuverture.has(heat.id);
                const modifiee = !figee && coche !== ouverteAvant;
                const publie = modifiee && coche
                  && (heat.status || '').toLowerCase() !== 'ready';

                return (
                  <TableRow
                    key={heat.id}
                    // Liseré blanc = ligne modifiée, pas encore appliquée.
                    className={cn(
                      figee && 'opacity-40',
                      modifiee && 'border-l-[3px] border-l-white/55 bg-white/[0.04]',
                    )}
                    title={figee ? t('heats.slotsDialog.lockedHint') : undefined}
                  >
                    <TableCell>
                      <Checkbox
                        checked={coche}
                        disabled={figee}
                        onCheckedChange={() => basculer(heat)}
                        aria-label={t('heats.slotsDialog.proposeAria', {
                          number: heat.heat_number,
                          time: heat.start_time?.slice(0, 5),
                        })}
                      />
                    </TableCell>
                    <TableCell className="font-semibold tabular-nums">
                      {heat.start_time?.slice(0, 5)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {heat.heat_name || `#${heat.heat_number}`}
                      {categorie === 'all' && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {nomCategorie(heat)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums text-muted-foreground">
                      {heat.participant_count || 0} / {heat.max_participants}
                    </TableCell>

                    {/* Statut : `avant → après` seulement sur une ligne modifiée.
                        La valeur sortante n'est barrée que si elle part VRAIMENT —
                        décocher ne dépublie pas, donc « Prêt → Prêt » sans barré. */}
                    <TableCell>
                      {figee ? (
                        <span className="text-xs text-muted-foreground">
                          {t('heats.slotsDialog.locked')}
                        </span>
                      ) : modifiee ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Pastille libelle={libelleStatut(heat)} sortante barre={publie} />
                          <Fleche />
                          <Pastille libelle={publie ? t('heats.statusLabels.ready') : libelleStatut(heat)} />
                        </span>
                      ) : (
                        <Pastille libelle={libelleStatut(heat)} />
                      )}
                    </TableCell>

                    <TableCell>
                      {modifiee ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Porte ouverte={ouverteAvant} />
                          <Fleche />
                          <Porte ouverte={coche} />
                        </span>
                      ) : (
                        <Porte ouverte={coche} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Légende : mêmes icônes que le planning, pour qu'on les reconnaisse là-bas. */}
        <div className="flex flex-wrap items-center gap-5 rounded border border-border bg-muted/30 px-3 py-2 text-xs">
          <span className="inline-flex items-center gap-2 text-success">
            <DoorOpen className="h-4 w-4" aria-hidden />
            {t('heats.slotsDialog.legendOpen')}
          </span>
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <DoorClosed className="h-4 w-4" aria-hidden />
            {t('heats.slotsDialog.legendClosed')}
          </span>
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <span className="block h-4 w-[3px] rounded-sm bg-white/55" aria-hidden />
            {t('heats.slotsDialog.legendModified')}
          </span>
          <span className="ml-auto text-muted-foreground">
            {t('heats.slotsDialog.samePlanningIcons')}
          </span>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed">
          {t('heats.slotsDialog.uncheckNote')}
        </p>

        <DialogFooter className="items-center gap-2 sm:gap-4">
          <span className="mr-auto text-xs text-muted-foreground">
            {t('heats.slotsDialog.summary', {
              opened: aOuvrir.length,
              published: aPublier,
              closed: aFermer.length,
            })}
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={rien || loading}
            onClick={() => onConfirm(aOuvrir.map(h => h.id), aFermer.map(h => h.id))}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SlotsDialog;
